/**
 * ChatComposer — input area at the bottom of the chat panel.
 *
 * Shows the active ad selection count and a text input with send button.
 * Supports @-mention autocompletion via MentionDropdown.
 */

import { useState, useRef, useCallback } from 'react';
import { MentionDropdown } from './MentionDropdown';
import type { MentionItem } from './MentionDropdown';

export interface MentionRef {
  type: string;
  id: string;
  label: string;
}

interface ChatComposerProps {
  activeAdCount: number;
  isStreaming: boolean;
  onSend: (content: string, mentions: MentionRef[]) => void;
  onCancel: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  totalAdCount: number;
  mentionItems?: MentionItem[];
}

export function ChatComposer({
  activeAdCount,
  isStreaming,
  onSend,
  onCancel,
  onSelectAll,
  onClearSelection,
  totalAdCount,
  mentionItems = [],
}: ChatComposerProps) {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<MentionRef[]>([]);
  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    if (!text.trim() || isStreaming) return;
    onSend(text.trim(), mentions);
    setText('');
    setMentions([]);
    inputRef.current?.focus();
  }, [text, isStreaming, onSend, mentions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Let the dropdown consume arrow / enter / escape while open
      if (isMentioning && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, isMentioning],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursor = e.target.selectionStart ?? value.length;
      setText(value);

      if (isMentioning) {
        // Check if the user deleted the trigger `@`
        if (mentionStartIndex >= value.length || value[mentionStartIndex] !== '@') {
          setIsMentioning(false);
          setMentionQuery('');
          return;
        }
        const query = value.slice(mentionStartIndex + 1, cursor);
        // Close if user typed a space right after @
        if (query.includes(' ') && query.trim() === '') {
          setIsMentioning(false);
          setMentionQuery('');
          return;
        }
        setMentionQuery(query);
      } else {
        // Detect a fresh `@` trigger
        const charBefore = cursor >= 2 ? value[cursor - 2] : undefined;
        if (
          value[cursor - 1] === '@' &&
          (charBefore === undefined || charBefore === ' ' || charBefore === '\n')
        ) {
          setIsMentioning(true);
          setMentionStartIndex(cursor - 1);
          setMentionQuery('');
        }
      }
    },
    [isMentioning, mentionStartIndex],
  );

  const handleMentionSelect = useCallback(
    (item: MentionItem) => {
      const before = text.slice(0, mentionStartIndex);
      const after = text.slice(inputRef.current?.selectionStart ?? text.length);
      const insertText = `@${item.label} `;
      setText(before + insertText + after);

      setMentions((prev) => [...prev, { type: item.type, id: item.id, label: item.label }]);
      setIsMentioning(false);
      setMentionQuery('');

      // Re-focus and set cursor after the inserted mention
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          const pos = before.length + insertText.length;
          el.focus();
          el.setSelectionRange(pos, pos);
        }
      });
    },
    [text, mentionStartIndex],
  );

  const handleMentionClose = useCallback(() => {
    setIsMentioning(false);
    setMentionQuery('');
  }, []);

  return (
    <div className="border-t border-slate-200 bg-white p-3">
      {/* Selection indicator */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
            {activeAdCount === 0
              ? 'No ads selected'
              : activeAdCount === totalAdCount
                ? 'All ads selected'
                : `${activeAdCount} ad${activeAdCount !== 1 ? 's' : ''} selected`}
          </span>
          {activeAdCount > 0 && activeAdCount < totalAdCount && (
            <button
              type="button"
              onClick={onSelectAll}
              className="text-[10px] font-mono text-brand-teal-dark hover:text-brand-teal transition"
            >
              select all
            </button>
          )}
          {activeAdCount > 0 && (
            <button
              type="button"
              onClick={onClearSelection}
              className="text-[10px] font-mono text-slate-400 hover:text-slate-600 transition"
            >
              clear
            </button>
          )}
        </div>
        {activeAdCount > 0 && (
          <div className="flex -space-x-1">
            {Array.from({ length: Math.min(activeAdCount, 5) }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-brand-teal border border-white"
              />
            ))}
            {activeAdCount > 5 && (
              <span className="text-[9px] font-mono text-slate-400 ml-1">
                +{activeAdCount - 5}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="relative flex items-end gap-2">
        {/* Mention dropdown — positioned above the textarea */}
        <MentionDropdown
          items={mentionItems}
          query={mentionQuery}
          onSelect={handleMentionSelect}
          onClose={handleMentionClose}
          visible={isMentioning}
        />

        <textarea
          ref={inputRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            activeAdCount === 0
              ? 'Select ads first, then describe edits...'
              : 'Describe edits to apply... (type @ to mention)'
          }
          disabled={isStreaming}
          rows={1}
          className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/30 resize-none disabled:opacity-50 min-h-[38px] max-h-[120px]"
          style={{ fieldSizing: 'content' } as React.CSSProperties}
        />
        {isStreaming ? (
          <button
            type="button"
            onClick={onCancel}
            className="shrink-0 h-[38px] px-3 rounded-lg bg-red-50 text-danger border border-red-200 text-xs font-mono uppercase tracking-wider hover:bg-red-100 transition"
          >
            Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!text.trim() || activeAdCount === 0}
            className="shrink-0 h-[38px] px-4 rounded-lg bg-brand-teal text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand-teal-dark disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            Send
          </button>
        )}
      </div>
    </div>
  );
}
